import type {ToolWorkContext} from "./ToolWorkLifecycleTypes";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import type {EnginePayload, ToolConfig, TimelineSchemaEngineInterface, TimelineEventEngineInterface, TimelineOrderingEngineInterface} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {TimelineSchemaEngineInterfaceToken, TimelineEventEngineInterfaceToken, TimelineOrderingEngineInterfaceToken} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {ToolEngineValidationResult} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";
import {normalizeTimelineLinks} from "@shared/GenericTools/timelineCanvas/TimelineCanvasModel";

const text = (value: any) => String(value ?? "")
const rows = (value: any) => Array.isArray(value) ? value : []
const dateBoundary = (value: any, format: any, isEnd: boolean) => {
	const date = text(value)
	if (format === "YYYY") return `${date.slice(0, 4)}-${isEnd ? "12-31" : "01-01"}`
	if (format === "MMM YYYY") return `${date.slice(0, 7)}-${isEnd ? "31" : "01"}`
	return date
}

export default class TimelineToolEngine extends BaseToolEngine implements TimelineSchemaEngineInterface, TimelineEventEngineInterface, TimelineOrderingEngineInterface {

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext, isSubmission: boolean): EnginePayload {
		if (isSubmission) this.assertWorkValidation(this.validateTimelinePayload(payload, config))
		return this.normalizePayload(payload, config)
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig): ToolEngineValidationResult {
		return this.validateTimelinePayload(payload, config)
	}

	getEngineInterfaceTokens() {return [TimelineSchemaEngineInterfaceToken, TimelineEventEngineInterfaceToken, TimelineOrderingEngineInterfaceToken]}

	normalizeConfig(config: ToolConfig = {}): ToolConfig {
		return {
			...config,
			title: text(config.title), instructions: text(config.instructions), reviewInstructions: text(config.reviewInstructions),
			entryPrompt: text(config.entryPrompt), notesPrompt: text(config.notesPrompt), hypothesisPrompt: text(config.hypothesisPrompt),
			categoryQuestion: text(config.categoryQuestion || "Entry category"), timingQuestion: text(config.timingQuestion || "Reported timing"),
			exactDateQuestion: text(config.exactDateQuestion || "Date"), approximateQuestion: text(config.approximateQuestion || "Approximate period"), relativeQuestion: text(config.relativeQuestion || "Relative timing"),
			interpretationTitle: text(config.interpretationTitle || "Practitioner interpretation"),
			uncertaintyQuestion: text(config.uncertaintyQuestion || "Is the timing or account uncertain?"), uncertaintyLabel: text(config.uncertaintyLabel || "Timing or account uncertain"),
			categories: rows(config.categories).map((row: ToolConfig, index) => ({id: text(row.id || `category_${index}`), title: text(row.title), color: text(row.color), kind: ["event", "transition", "symptom"].includes(text(row.kind)) ? row.kind : "event"})),
			timingOptions: rows(config.timingOptions).map((row: ToolConfig, index) => ({id: text(row.id || `timing_${index}`), title: text(row.title), kind: ["exact", "approximate", "relative"].includes(text(row.kind)) ? row.kind : "relative"})),
			fields: rows(config.fields).map((row: ToolConfig) => ({...row, id: text(row.id), title: text(row.title), type: text(row.type || "text")})),
		}
	}

	getTimelineResultConfig(payload: EnginePayload = {}, config?: ToolConfig): ToolConfig {
		const saved = (payload.timeline as ToolConfig | undefined)?.config as ToolConfig || {}
		const current = this.normalizeConfig(config ?? saved)
		for (const [key, archiveKey] of [["fields", "archivedFields"], ["categories", "archivedCategories"], ["timingOptions", "archivedTimingOptions"]]) {
			const activeIds = new Set(rows(current[key]).map(row => row.id))
			const candidates = [...rows(saved[archiveKey]), ...rows(saved[key]), ...rows(current[archiveKey])]
			current[archiveKey] = Array.from(new Map(candidates.filter(row => !activeIds.has(row.id)).map(row => [row.id, row])).values())
		}
		return current
	}

	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		const normalized = this.normalizeConfig(config)
		for (const name of ["categories", "timingOptions", "fields"]) {
			const list = normalized[name] as ToolConfig[]
			const ids = list.map(row => row.id)
			if (ids.some(id => !id) || new Set(ids).size !== ids.length || list.some(row => !text(row.title).trim())) result.errors.push(`Invalid timeline ${name}`)
		}
		result.isValid = result.errors.length === 0
		return result
	}

	createEvent(event: ToolConfig = {}): ToolConfig {
		return {...event, id: text(event.id || `event_${Date.now()}_${Math.random().toString(36).slice(2)}`), label: text(event.label || event.description), categoryId: text(event.categoryId), timingOptionId: text(event.timingOptionId), exactDate: text(event.exactDate), exactDateFormat: text(event.exactDateFormat), isPeriod: !!event.isPeriod, periodStart: text(event.periodStart), periodStartFormat: text(event.periodStartFormat), periodEnd: text(event.periodEnd), periodEndFormat: text(event.periodEndFormat), approximatePeriod: text(event.approximatePeriod), relativeText: text(event.relativeText), notes: text(event.notes), isUncertain: !!event.isUncertain, order: Number.isFinite(Number(event.order)) ? Number(event.order) : 0}
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {return {timeline: {config: this.normalizeConfig(config), events: [], links: []}}}

	normalizePayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		const timeline = (payload.timeline as ToolConfig) || {}
		const effectiveConfig = this.normalizeConfig(config)
		const categories = new Map((effectiveConfig.categories as ToolConfig[]).map(item => [item.id, item.title]))
		const timingOptions = new Map((effectiveConfig.timingOptions as ToolConfig[]).map(item => [item.id, item.title]))
		const events = rows(timeline.events).map((value: ToolConfig) => {
			const event = this.createEvent(value)
			return {...event, categoryTitle: text(categories.get(event.categoryId) || event.categoryTitle), timingTitle: text(timingOptions.get(event.timingOptionId) || event.timingTitle)}
		})
		const links = normalizeTimelineLinks(timeline.links, events)
		return {...payload, timeline: {...timeline, config: effectiveConfig, events, links}}
	}

	validateTimelinePayload(payload: EnginePayload = {}, config: ToolConfig = {}): ToolEngineValidationResult {
		const result = this.validateConfig(config)
		const timeline = payload.timeline as ToolConfig | undefined
		const events = timeline?.events as ToolConfig[] | undefined
		if (!Array.isArray(events)) result.errors.push("Invalid timeline events")
		else {
			const ids = events.map(event => event.id)
			if (new Set(ids).size !== ids.length || events.some(event => !event.id || (typeof event.label !== "string" && typeof event.description !== "string"))) result.errors.push("Invalid timeline event")
			const timingKinds = new Map((this.normalizeConfig(config).timingOptions as ToolConfig[]).map(option => [option.id, option.kind]))
			if (events.some(event => {
				if (event.isPeriod || timingKinds.get(event.timingOptionId) !== "exact" || !event.exactDate) return false
				const value = text(event.exactDate)
				const parsed = new Date(value)
				return !/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value
			})) result.errors.push("Invalid exact date")
			const isValidDate = (value: unknown) => {
				if (!value) return true
				const date = text(value)
				const parsed = new Date(date)
				return /^\d{4}-\d{2}-\d{2}$/.test(date) && Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === date
			}
			if (events.some(event => event.isPeriod ? !isValidDate(event.periodStart) || !isValidDate(event.periodEnd) || !!(event.periodStart && event.periodEnd && dateBoundary(event.periodEnd, event.periodEndFormat, true) < dateBoundary(event.periodStart, event.periodStartFormat, false)) : !isValidDate(event.exactDate))) result.errors.push("Invalid event date or period")
		}
		result.isValid = result.errors.length === 0
		return result
	}

	createSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const events = this.getOrderedEvents(normalized)
		return {
			timeline: {...normalized.timeline as ToolConfig, events},
			toolTitle: text(config.title), headline: text(config.title), eventCount: events.length,
			uncertainCount: events.filter(event => event.isUncertain || event.uncertainty || (!event.exactDate && !event.periodStart && !event.periodEnd && !event.approximatePeriod && !event.relativeText)).length,
			followUpCount: events.filter(event => event.followUp).length,
		}
	}

	updateEvent(payload: EnginePayload = {}, event: ToolConfig = {}, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.timeline as ToolConfig
		const events = rows(timeline.events)
		const index = events.findIndex(item => item.id === event.id)
		const next = this.createEvent({...event, order: index < 0 ? events.length : event.order})
		if (index < 0) events.push(next)
		else events.splice(index, 1, next)
		return this.normalizePayload({...normalized, timeline: {...timeline, events}}, config)
	}

	removeEvent(payload: EnginePayload = {}, eventId = "", config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.timeline as ToolConfig
		return {...normalized, timeline: {...timeline, events: rows(timeline.events).filter(event => event.id !== eventId), links: rows(timeline.links).filter(link => link.sourceEventId !== eventId && link.targetEventId !== eventId)}}
	}

	getOrderedEvents(payload: EnginePayload = {}): ToolConfig[] {
		const timeline = (payload.timeline as ToolConfig | undefined) || {}
		const events = rows(timeline.events).map(event => this.createEvent(event))
		if (timeline.sequenceMode === "array") return events
		const kinds = new Map(rows((timeline.config as ToolConfig | undefined)?.timingOptions).map((option: ToolConfig) => [option.id, option.kind]))
		const timing = (event: ToolConfig) => {
			const exact = text(event.exactDate)
			if (/^\d{4}-\d{2}-\d{2}$/.test(exact) && (!kinds.size || kinds.get(event.timingOptionId) === "exact")) return {year: exact.slice(0, 4), exact}
			if (kinds.size) return null
			const year = text(event.approximatePeriod).match(/\b(?:19|20)\d{2}\b/)
			return year ? {year: year[0], exact: ""} : null
		}
		const ordered = events.map((event, index) => ({event, index})).sort((a, b) => Number(a.event.order) - Number(b.event.order) || a.index - b.index)
		const dated = ordered.filter(item => timing(item.event)).sort((a, b) => {
			const left = timing(a.event)!, right = timing(b.event)!
			if (left.year !== right.year) return left.year.localeCompare(right.year)
			if (left.exact && right.exact && left.exact !== right.exact) return left.exact.localeCompare(right.exact)
			return Number(a.event.order) - Number(b.event.order) || a.index - b.index
		})
		let dateIndex = 0
		return ordered.map(item => timing(item.event) ? dated[dateIndex++].event : item.event)
	}

	moveEvent(payload: EnginePayload = {}, eventId = "", direction = 0, config: ToolConfig = {}): EnginePayload {
		const normalized = this.normalizePayload(payload, config)
		const timeline = normalized.timeline as ToolConfig
		const events = this.getOrderedEvents(normalized)
		const index = events.findIndex(event => event.id === eventId)
		const target = index + direction
		if (index >= 0 && target >= 0 && target < events.length) {
			const [event] = events.splice(index, 1)
			events.splice(target, 0, event)
		}
		return {...normalized, timeline: {...timeline, events: events.map((event, order) => ({...event, order}))}}
	}

	getTemporalRelations(payload: EnginePayload = {}): ToolConfig[] {
		const timeline = (payload.timeline as ToolConfig | undefined) || {}
		const kinds = new Map(rows((timeline.config as ToolConfig | undefined)?.timingOptions).map((option: ToolConfig) => [option.id, option.kind]))
		const events = this.getOrderedEvents(payload)
		const relations: ToolConfig[] = []
		for (let index = 1; index < events.length; index++) {
			const before = events[index - 1], after = events[index]
			if (before.isPeriod || after.isPeriod || kinds.get(before.timingOptionId) !== "exact" || kinds.get(after.timingOptionId) !== "exact") continue
			if (!/^\d{4}-\d{2}-\d{2}$/.test(text(before.exactDate)) || !/^\d{4}-\d{2}-\d{2}$/.test(text(after.exactDate))) continue
			const days = Math.round((Date.parse(text(after.exactDate)) - Date.parse(text(before.exactDate))) / 86400000)
			if (days >= 0) relations.push({beforeId: before.id, afterId: after.id, days})
		}
		return relations
	}
}
